import React from "react"
import {Controller, FieldPath, FieldValues} from "react-hook-form"
import {CalendarIcon} from "@heroicons/react/24/solid"
import {twMerge} from "tailwind-merge"

import {getCurrentLanguage} from "../../i18n"
import {useClickOutsideRef} from "../../utils/hooks"
import {pickProps} from "../../utils/types"
import Calendar from "../Calendar"
import {TBaseSharedProps, TConnectedField} from "../fields/components"
import {FieldLabel, pickFieldLabelProps, TFieldLabelProps} from "../fields/FieldLabel"
import {InputBase, TInputBaseProps} from "../fields/Input"

export type TDateSharedProps = TBaseSharedProps

export type TDateBaseProps = Omit<TInputBaseProps, "onChange" | "value"> & {
  onChange: (value: Date) => void
  value: Date
  minDate?: Date
  maxDate?: Date
  calendarClassName?: string
}

export type TDateConnectedProps<T extends FieldValues, N extends FieldPath<T>> = TDateSharedProps &
  TConnectedField<T, N>

export const DateBase = React.forwardRef<HTMLInputElement, TDateBaseProps>(
  ({value, onChange, readOnly, disabled, onFocus, Icon, minDate, maxDate, calendarClassName, ...props}, ref) => {
    const [open, setOpen] = React.useState(false)

    const close = React.useCallback(() => setOpen(false), [])
    const clickOutsideRef = useClickOutsideRef<HTMLDivElement>(close)

    const handleSelect = (selected: Date) => {
      !readOnly && !disabled && onChange?.(selected)
      close()
    }

    const handleClick = () => {
      !disabled && setOpen(true)
    }

    return (
      <div ref={clickOutsideRef}>
        <InputBase
          value={value?.toLocaleDateString(getCurrentLanguage()) ?? ""}
          onFocus={e => {
            handleClick()
            onFocus?.(e)
          }}
          Icon={Icon ?? CalendarIcon}
          ref={ref}
          disabled={disabled}
          readOnly={readOnly}
          onChange={() => undefined}
          {...props}
        />
        {open && (
          <div className={"relative"}>
            <div className={twMerge("absolute top-2 right-0 z-20 w-96 card p-4", calendarClassName)}>
              <Calendar value={value} onSelect={handleSelect} beginning={value} minDate={minDate} maxDate={maxDate} />
            </div>
          </div>
        )}
      </div>
    )
  }
)
DateBase.displayName = "Date"

function DateConnected<T extends FieldValues, N extends FieldPath<T>>(props: TDateConnectedProps<T, N>) {
  return (
    <Controller
      name={props.name}
      render={({field, fieldState: {error}}) => <DateBase {...field} hasError={!!error} {...props} />}
    />
  )
}

export function DateField<T extends FieldValues, N extends FieldPath<T>>(
  props: TFieldLabelProps & TDateConnectedProps<T, N>
) {
  const fieldLabelProps = pickFieldLabelProps(props)
  const inputProps = pickDateConnectedProps(props)

  return (
    <FieldLabel {...fieldLabelProps}>
      <DateConnected {...inputProps} />
    </FieldLabel>
  )
}

function pickDateConnectedProps<T extends FieldValues, N extends FieldPath<T>>(
  props: React.ComponentProps<typeof DateField<T, N>>
) {
  return pickProps<TDateConnectedProps<T, N>>({
    name: true,
    disabled: true,
    hasError: true,
    options: true,
    readOnly: true,
  })(props)
}
